import React from 'react';
import { useFormContext } from 'react-hook-form';
import { commonUtils, Headline } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { ContentfulEntry } from '@/contentful/components/ContentfulEntry';
import { StaticContentfulEntry } from '@/contentful/contentful.constants';
import { IWidget, ParsedEntry } from '@/contentful/types';

import { HookFormBooleanSelectionTile } from '@components/ReactHookFormFields/HookFormBooleanSelectionTile';
import { HookFormDateTimePicker } from '@components/ReactHookFormFields/HookFormDateTimePicker';
import { HookFormDropdown } from '@components/ReactHookFormFields/HookFormDropdown';
import { HookFormFileInput } from '@components/ReactHookFormFields/HookFormFileInput';
import { HookFormTextArea } from '@components/ReactHookFormFields/HookFormTextArea';
import { HookFormTextInput } from '@components/ReactHookFormFields/HookFormTextInput';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { DisturbanceReason } from '@models/api/Incident';
import { apartmentStore } from '@stores/apartmentStore';
import {
  PortalContent,
  PortalId,
} from '@views/MessagesPage/components/PortalContent';

import { DisturbanceReportFormBase } from './Disturbance.utils';

const currentTime = new Date();

/** Form used by all Disturbance categories in addition to their own form fields */
export const DisturbanceCommonForm = observer(
  ({ customTranslations }: ParsedEntry<IWidget>) => {
    const { watch } = useFormContext<DisturbanceReportFormBase>();

    const disturbanceReason = watch('disturbanceReason');

    const { t, tContentful } = useCustomTranslations(customTranslations);

    // display smoking info text if ...
    const isSmokingInfoTextVisible =
      // ... smoking disturbance reason is selected and ...
      disturbanceReason === DisturbanceReason.SMOKING &&
      // ... selected apartment has smoke-free data defined
      commonUtils.nonNullable(
        apartmentStore.selectedApartment?.apartment.anomalySmokeFree ??
          apartmentStore.selectedApartment?.realestate.smokeFree
      );

    return (
      <>
        <PortalContent portalId={PortalId.MESSAGE_PAGE_HEADER_CONTENT}>
          <div className="mb-5 flex w-full flex-col gap-4">
            <HookFormDropdown
              name="disturbanceReason"
              label={t('disturbanceReasonLabel')}
              items={Object.values(DisturbanceReason).map((reasonKey) => ({
                label: tContentful(`disturbanceReason.${reasonKey}`),
                value: reasonKey,
              }))}
              floatingLabel
              isRequired
            />

            {isSmokingInfoTextVisible && (
              <ContentfulEntry
                entryId={StaticContentfulEntry.SMOKING_DISTURBANCE_REASON_INFO}
                className="h-[10ch]"
              />
            )}
          </div>
        </PortalContent>

        <HookFormTextInput
          name="disturbancePerson"
          label={{ value: t('disturbancePersonLabel'), type: 'static' }}
          maxLength={150}
        />

        <HookFormDateTimePicker
          name="disturbanceTimes"
          label={t('dateTimeLabel')}
          multiSelection
          maxDate={currentTime}
        />

        <HookFormBooleanSelectionTile
          name="discussed"
          label={t('discussedLabel')}
        />

        <HookFormBooleanSelectionTile
          name="policeCalled"
          label={t('policeCalledLabel')}
        />

        <HookFormBooleanSelectionTile
          name="reportedBefore"
          label={t('reportedBeforeLabel')}
        />

        <Headline level={3}>{t('descriptionTitle')}</Headline>

        <HookFormTextArea
          name="description"
          label={{ value: t('descriptionLabel'), type: 'static' }}
          rows={6}
          placeholder={t('descriptionPlaceholder')}
          message={t('descriptionHelper')}
          maxLength={1800}
        />

        <HookFormFileInput
          accept="application/pdf,image/jpeg,image/png"
          name="files"
          message={tContentful('supported_attachment_files')}
          label={tContentful('choose_files')}
        />
      </>
    );
  }
);
